import { useEffect, useRef, useState } from "react";
import type { CanvasEnv } from "@canvas-sync/shared";
import { useCanvasEnv } from "../lib/envContext";
import { ConfirmDialog } from "./ConfirmDialog";

// Display-only Canvas hostnames for the env menu, sourced from build config
// (VITE_CANVAS_TEST_HOST / VITE_CANVAS_PROD_HOST per env). Falls back to empty
// so no org-specific hostname is hardcoded in committed source.
const TEST_HOST = import.meta.env.VITE_CANVAS_TEST_HOST ?? "";
const PROD_HOST = import.meta.env.VITE_CANVAS_PROD_HOST ?? "";

export function EnvSwitcher() {
  const { env, setEnv } = useCanvasEnv();
  const [open, setOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<CanvasEnv | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Close menu on outside click.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  function pick(target: CanvasEnv) {
    setOpen(false);
    if (target === env) return;
    if (target === "prod") {
      setConfirmTarget("prod");
    } else {
      setEnv(target);
    }
  }

  return (
    <div className="env-switcher" ref={ref}>
      <button
        className={`env-pill env-pill-${env}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`env-dot env-dot-${env}`} />
        <span className="env-pill-label">{env.toUpperCase()}</span>
        <span className="env-pill-caret">▾</span>
      </button>

      {open && (
        <div className="env-menu" role="listbox">
          <button
            className={
              "env-menu-item" + (env === "test" ? " env-menu-item-current" : "")
            }
            onClick={() => pick("test")}
            role="option"
            aria-selected={env === "test"}
          >
            <span className="env-dot env-dot-test" />
            <span>TEST</span>
            <span className="env-menu-desc">{TEST_HOST}</span>
          </button>
          <button
            className={
              "env-menu-item" + (env === "prod" ? " env-menu-item-current" : "")
            }
            onClick={() => pick("prod")}
            role="option"
            aria-selected={env === "prod"}
          >
            <span className="env-dot env-dot-prod" />
            <span>PROD</span>
            <span className="env-menu-desc">
              {PROD_HOST ? `${PROD_HOST} · real data` : "real data"}
            </span>
          </button>
        </div>
      )}

      {confirmTarget === "prod" && (
        <ConfirmDialog
          title="Switch to production Canvas"
          message="The dashboard will switch to production Canvas. Anything you submit from here — including from the Manual upload page — will change real Canvas data and can't be undone. Are you sure?"
          confirmPhrase="SWITCH TO PROD"
          confirmLabel="Switch to production"
          danger
          onConfirm={() => {
            setEnv("prod");
            setConfirmTarget(null);
          }}
          onCancel={() => setConfirmTarget(null)}
        />
      )}
    </div>
  );
}
